import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

import Login from './pages/Login';
import Register from './pages/Register';
import RegistrationSuccess from './pages/RegistrationSuccess';
import ResetPassword from './pages/ResetPassword';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminBusinesses from './pages/AdminBusinesses';
import AdminCoupons from './pages/AdminCoupons';
import AdminUsers from './pages/AdminUsers';
import SafeMethods from './pages/SafeMethods';
import DailyDiary from './pages/DailyDiary';
import Temperature from './pages/Temperature';
import Suppliers from './pages/Suppliers';
import Training from './pages/Training';
import Incidents from './pages/Incidents';
import Reviews from './pages/Reviews';
import Reports from './pages/Reports';
import UserManagement from './pages/UserManagement';
import Allergens from './pages/Allergens';
import Settings from './pages/Settings';
import CalibrationLogs from './pages/CalibrationLogs';
import DeliveryRecords from './pages/DeliveryRecords';
import StorageLabels from './pages/StorageLabels';
import SavedItems from './pages/SavedItems';

import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
    const { isAuthenticated, loading, user } = useAuth();
    if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (allowedRoles && !allowedRoles.includes(user?.role)) return <Navigate to={user?.role === 'admin' ? '/admin' : '/dashboard'} replace />;
    return children;
};

const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();
    if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
    if (isAuthenticated) return <Navigate to={user?.role === 'admin' ? '/admin' : '/dashboard'} replace />;
    return children;
};

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/registration-success" element={<RegistrationSuccess />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/businesses" element={<ProtectedRoute allowedRoles={['admin']}><AdminBusinesses /></ProtectedRoute>} />
            <Route path="/admin/coupons" element={<ProtectedRoute allowedRoles={['admin']}><AdminCoupons /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><AdminUsers /></ProtectedRoute>} />

            {/* Business Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/safe-methods" element={<ProtectedRoute><SafeMethods /></ProtectedRoute>} />
            <Route path="/diary" element={<ProtectedRoute><DailyDiary /></ProtectedRoute>} />
            <Route path="/temperature" element={<ProtectedRoute><Temperature /></ProtectedRoute>} />
            <Route path="/saved-items" element={<ProtectedRoute><SavedItems /></ProtectedRoute>} />
            <Route path="/allergens" element={<ProtectedRoute><Allergens /></ProtectedRoute>} />
            <Route path="/suppliers" element={<ProtectedRoute><Suppliers /></ProtectedRoute>} />
            <Route path="/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
            <Route path="/incidents" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />
            <Route path="/calibration" element={<ProtectedRoute><CalibrationLogs /></ProtectedRoute>} />
            <Route path="/deliveries" element={<ProtectedRoute><DeliveryRecords /></ProtectedRoute>} />
            <Route path="/labels" element={<ProtectedRoute><StorageLabels /></ProtectedRoute>} />
            <Route path="/reviews" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><Reviews /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><Reports /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><UserManagement /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><Settings /></ProtectedRoute>} />

            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster position="top-right" toastOptions={{ style: { background: '#1E293B', border: '1px solid #334155', color: '#F8FAFC' } }} />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
